import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { handleBackgroundScroll } from "../utils/backgroundScroll";
import { CartData } from "../types/cart.types";
import { getCart } from "../services/cartService";
import { useUserContext } from "./UserContext";
import { updateCart, deleteCart } from "../services/cartService";
import {
    parseDMY,
    formatDMY,
    getDateISO,
    isAnyDateOlderThanTomorrow,
    normalizeDate,
    isOnOrAfterEffectiveTomorrow,
} from "../utils/calendar";
import { usePublicCatalog } from "./PublicCatalogContext";
import { useNavigate, useLocation } from "react-router-dom";
import { log, error as logError } from "../utils/logger";

/**
* Shape of the Cart Context value.
*/
interface CartContextType {
    /** Whether the cart UI is currently open */
    cartOpen: boolean;
    /** Open the cart */
    openCart: () => void;
    /** Close the cart */
    closeCart: () => void;
    /** Current cart data */
    cartData: CartData | null;
    /** Whether cart is loaded */
    cartLoaded: boolean;
    /** Reload cart data */
    reloadCart: () => Promise<void>;
    /** Clear cart data */
    clearCart: () => void;
    /** Cart total price without tax (from server) */
    cartPrice: number;
    /** Cart price with tax (from server) */
    cartPriceWithTax: number;
    /** Total tax amount */
    totalTax: number;
    /** Total item count */
    totalAmount: number;
}

/**
* React context to manage cart state and actions.
* Provides cart data, prices, and methods to modify the cart UI and content.
*/
const CartContext = createContext<CartContextType | undefined>(undefined);

/**
* Provider component that wraps children with cart state.
* 
* @param children - React child elements
* @param products - List of available products
* @param items - Storage items relevant for price calculations
*/
export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    // UI State
    const [cartOpen, setCartOpen] = useState(false);
    const { isPublicView} = usePublicCatalog();
    const { loggedIn } = useUserContext();
    
    // Cart Data State
    const [cartData, setCartData] = useState<CartData | null>(null);
    const [cartLoaded, setCartLoaded] = useState<boolean>(false);

    // Cart Actions
    const openCart = () => setCartOpen(true);
    const closeCart = () => setCartOpen(false);

    const navigate = useNavigate();
    const location = useLocation();
    const isOrderConfirmation = location.pathname.startsWith("/order-confirmation");
    const isPublicCatalog = location.pathname.startsWith("/public-catalog");

    // Prevent redirect loops/spam (and don't redirect if you're already on /order-settings)
    const safeNavigateToOrderSettings = useCallback(() => {
        if (location.pathname === "/order-settings") return;
        if (isOrderConfirmation) return;
        navigate("/order-settings", { replace: true });
    }, [location.pathname, isOrderConfirmation, navigate]);

    // Reload cart function
    const reloadCart = useCallback(async () => {
        if (isPublicView || isPublicCatalog) {
            setCartData(null);
            setCartLoaded(true);
            return;
        }
        if (!loggedIn) return;

        try {
            const data = await getCart();

            // if cart missing
            if (!data) {
                setCartData(null);
                setCartLoaded(true);
                safeNavigateToOrderSettings();
                return;
            }

            // your existing check
            const isOlder = isAnyDateOlderThanTomorrow(data?.selected_dates ?? []);
            if (isOlder) {
                await deleteCart();
                setCartData(null);
                setCartLoaded(true);
                safeNavigateToOrderSettings();
                return;
            }

            // IMPORTANT: don't block on delivery_adress.city
            // Your carts often don't have it, so it causes false redirects.
            const datesMissing = !data.selected_dates?.length;
            const addressMissing = !data.delivery_adress?.adress?.trim();

            // Keep cart context up-to-date
            setCartData(data);
            setCartLoaded(true);

            if (datesMissing || addressMissing) {
                safeNavigateToOrderSettings();
                return;
            }

            // valid
        } catch (error) {
            logError("Error reloading cart:", error);
            setCartData(null);
            setCartLoaded(true);
            safeNavigateToOrderSettings();
        }
    }, [isPublicCatalog, isPublicView, loggedIn, safeNavigateToOrderSettings]);

    // Initial cart load
    useEffect(() => {
        reloadCart();
    }, [reloadCart]);

    // Get prices from cart data (no calculation needed)
    const cartPrice = cartData?.totals?.total_price_without_tax || 0;
    const cartPriceWithTax = cartData?.totals?.total_price_with_tax || 0;
    const totalTax = cartData?.totals?.total_tax || 0;
    const totalAmount = cartData?.totals?.total_amount || 0;

    // Stop scroll on open
    useEffect(() => {
        handleBackgroundScroll(cartOpen);
    }, [cartOpen]);

    const clearCart = () => {
        setCartData(null)
    }

    // Prevent endless auto-fix loops
    const fixingDatesRef = useRef(false);

    // Handle updating selectedDates if theres a record at not selected date
    useEffect(() => {
        if (!cartLoaded || !cartData?.selected_dates) return;
        if (fixingDatesRef.current) return;

        const itemDates = Object.entries(cartData?.items || {})
            .filter(([date, arr]) => Array.isArray(arr) && arr.length > 0)
            .map(([date]) => date);

        const isValidDMY = (d: string) => isOnOrAfterEffectiveTomorrow(normalizeDate(d));
        const validSelectedDates = cartData.selected_dates.filter(isValidDMY);

        const missingDates = itemDates.filter(date => !(validSelectedDates.includes(date)));
        const validMissingDates = missingDates.filter(isValidDMY);

        const needsFix =
            validMissingDates.length > 0 ||
            validSelectedDates.length !== cartData.selected_dates.length;

        if (needsFix && cartLoaded && cartData && cartData.selected_dates) {
            fixingDatesRef.current = true;

            const formDates = [...validMissingDates, ...validSelectedDates];
            if (formDates.length === 0) {
                fixingDatesRef.current = false;
                return;
            }
            const formDatesSorted = formDates
                .map(d => parseDMY(d))       // convert "DD. M. YYYY" to Date
                .filter(Boolean) as Date[];  // remove any invalid dates

            formDatesSorted.sort((a, b) => a.getTime() - b.getTime());  // sort chronologically

            // if you want them back as "DD. M. YYYY" strings
            const formDatesSortedStr = formDatesSorted.map(d => formatDMY(d));
            
            const isoDates = formDatesSorted
                .map(d => getDateISO(d))
                .filter(Boolean) as string[];
            
            const handleUpdateCart = async () => {
                try {
                    await updateCart(
                        isoDates[0],
                        isoDates[isoDates.length - 1],
                        formDatesSortedStr,
                        cartData.delivery_adress.adress
                    );
                    // Reload cart data after updating 
                    await reloadCart();
                } catch (error) {
                    logError("Error updating cart:", error);
                } finally {
                    fixingDatesRef.current = false;
                }
            };

            handleUpdateCart(); 
        }
    }, [
        cartLoaded,
        cartData?.id,
        cartData?.selected_dates,
        cartData?.items,
        cartData?.delivery_adress?.adress,
        reloadCart
    ]);
    
    return (
        <CartContext.Provider value={{ 
            cartOpen, 
            openCart, 
            closeCart,
            cartData,
            cartLoaded,
            reloadCart,
            cartPrice,
            cartPriceWithTax,
            totalTax,
            totalAmount,
            clearCart
        }}>
            {children}
        </CartContext.Provider>
    );
};

/**
* Hook to access the CartContext value.
* Throws error if used outside of CartProvider.
* 
* @returns CartContextType object with cart data and handlers.
*/
export const useCartContext = () => {
    const context = useContext(CartContext);
    if (!context) throw new Error("useCartContext must be used within a CartProvider");
    return context;
};
